/* eslint-disable react-doctor/no-fetch-in-effect */
import { Button } from "@shadcn/ui/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useReducer } from "react";
import Logo from "@/src/presentation/components/logo";
import "./markdown.css";

type PolicyState = {
  htmlContent: string;
  loading: boolean;
  error: string | null;
};

type PolicyAction =
  | { type: "SET_CONTENT"; htmlContent: string }
  | { type: "SET_ERROR"; error: string }
  | { type: "SET_LOADED" };

function policyReducer(prev: PolicyState, action: PolicyAction): PolicyState {
  switch (action.type) {
    case "SET_CONTENT":
      return { htmlContent: action.htmlContent, loading: false, error: null };
    case "SET_ERROR":
      return { htmlContent: "", loading: false, error: action.error };
    case "SET_LOADED":
      return { ...prev, loading: false };
    default:
      return prev;
  }
}

// eslint-disable-next-line react/no-fetch-in-effect -- one-time data fetch at mount
function markdownToHtml(markdown: string): string {
  let html = markdown;

  html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
  html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
  html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

  html = html.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");

  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  html = html.replace(/\n\n/g, "</p><p>");
  html = `<p>${html}</p>`;

  html = html.replace(/---/g, "<hr />");

  html = html.replace(/^- (.*$)/gim, "<li>$1</li>");
  html = html.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

  html = html.replace(/^\d+\. (.*$)/gim, "<li>$1</li>");

  html = html.replace(/\|(.+)\|/g, (match) => {
    const cells = match.split("|").filter((c) => c.trim());
    if (cells.some((c) => c.includes("---"))) {
      return "";
    }
    const isHeader = cells.every((c) => c.trim().length > 0);
    const cellTags = cells
      .map((c) => (isHeader ? `<th>${c.trim()}</th>` : `<td>${c.trim()}</td>`))
      .join("");
    return isHeader ? `<tr>${cellTags}</tr>` : `<tr>${cellTags}</tr>`;
  });

  html = html.replace(/<p><\/p>/g, "");
  html = html.replace(/<p>(<h[1-6]>)/g, "$1");
  html = html.replace(/(<\/h[1-6]>)<\/p>/g, "$1");
  html = html.replace(/<p>(<ul>)/g, "$1");
  html = html.replace(/(<\/ul>)<\/p>/g, "$1");
  html = html.replace(/<p>(<hr \/>)/g, "$1");
  html = html.replace(/(<hr \/>)<\/p>/g, "$1");

  return html;
}

export const Route = createFileRoute("/_public/privacy-policy/")({
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  const [state, dispatch] = useReducer(policyReducer, {
    htmlContent: "",
    loading: true,
    error: null,
  });

  useEffect(() => {
    const fetchMarkdown = async () => {
      try {
        const response = await fetch("/privacy-policy.md");
        if (!response.ok) {
          throw new Error("Failed to load privacy policy");
        }
        let markdownContent = await response.text();

        const effectiveDate = "January 1, 2024";
        const lastUpdated = "August 15, 2025";

        markdownContent = markdownContent
          .replace(/\[EFFECTIVE_DATE\]/g, effectiveDate)
          .replace(/\[LAST_UPDATED\]/g, lastUpdated);

        const html = markdownToHtml(markdownContent);
        dispatch({ type: "SET_CONTENT", htmlContent: html });
      } catch (err) {
        dispatch({
          type: "SET_ERROR",
          error: err instanceof Error ? err.message : "Failed to load privacy policy",
        });
      }
    };

    fetchMarkdown();
  }, []);

  const { htmlContent, loading, error } = state;

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <Logo />
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading privacy policy…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <Logo />
          </div>
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-red-500 mb-4">{error}</div>
            <Button asChild variant="outline">
              <Link to="/login">
                <ArrowLeft className="mr-2 size-4" />
                Back to Login
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <Logo />
        </div>

        <div className="bg-white">
          {/* eslint-disable-next-line react/no-danger -- HTML is generated from fetched markdown, no user input */}
          <div className="markdown-content" dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>

        <div className="flex justify-center mt-8">
          <Button asChild variant="outline" className="px-8">
            <Link to="/login">
              <ArrowLeft className="mr-2 size-4" />
              Back to Login
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
